import { FormikHelpers, FormikState } from "formik";
import {
    OptionsObject,
    SnackbarKey,
    SnackbarMessage,
    useSnackbar,
} from "notistack";
import { ExtendedFirestoreInstance, useFirestore } from "react-redux-firebase";
import pickBy from "lodash/pickBy";

interface OnSubmitOptions<T> {
    beforeSubmit?: (value: T) => void;
    callback?: (
        id,
        resetForm: (nextState?: Partial<FormikState<T>>) => void
    ) => void;
    errorCallback?: (error) => void;
    successMessage?: SnackbarMessage;
    errorMessage?: SnackbarMessage;
}

const onSubmit = <T extends { id? }>(
    collection: string,
    firestore: ExtendedFirestoreInstance,
    enqueueSnackbar: (
        message: SnackbarMessage,
        options?: OptionsObject
    ) => SnackbarKey,
    options?: OnSubmitOptions<T>
) => (values: T, { setSubmitting, resetForm }: FormikHelpers<T>) => {
    setSubmitting(true);
    const {
        callback,
        errorCallback,
        successMessage,
        errorMessage,
        beforeSubmit,
    } = options || {};

    const { id, ...valuesWithoutId } = values;

    try {
        if (beforeSubmit) {
            beforeSubmit(values);
        }

        if (id) {
            firestore
                .collection(collection)
                .doc(id)
                .update(pickBy(valuesWithoutId, (value) => value !== undefined))
                .then(() => {
                    enqueueSnackbar(successMessage || "Dados atualizados!", {
                        variant: "success",
                    });
                    if (callback) {
                        callback(id, resetForm);
                    }
                    setSubmitting(false);
                })
                .catch((error) => {
                    console.error(
                        `Error updating values on collection ${collection}`,
                        error
                    );
                    enqueueSnackbar(
                        errorMessage || "Erro ao atualizar os dados!",
                        {
                            variant: "error",
                        }
                    );
                    if (errorCallback) {
                        errorCallback(error);
                    }
                    setSubmitting(false);
                });
        } else {
            firestore
                .collection(collection)
                .add(pickBy(valuesWithoutId, (value) => value !== undefined))
                .then((ref) => {
                    enqueueSnackbar(successMessage || "Dados salvos!", {
                        variant: "success",
                    });
                    if (callback) {
                        callback(ref.id, resetForm);
                    }
                    setSubmitting(false);
                })
                .catch((error) => {
                    console.error(
                        `Error saving new values on collection ${collection}`,
                        error
                    );
                    enqueueSnackbar(
                        errorMessage || "Erro ao adicionar os dados!",
                        {
                            variant: "error",
                        }
                    );
                    if (errorCallback) {
                        errorCallback(error);
                    }
                    setSubmitting(false);
                });
        }
    } catch (e) {
        setSubmitting(false);
        throw e;
    }
};

type OnSubmitFunction<T extends { id? }> = (
    values: T,
    { setSubmitting }: FormikHelpers<T>
) => void;

export const useOnSubmitFormik = <T extends { id? }>(
    collection: string,
    options?: OnSubmitOptions<T>
): OnSubmitFunction<T> => {
    const firestore = useFirestore();
    const { enqueueSnackbar } = useSnackbar();
    return onSubmit(collection, firestore, enqueueSnackbar, options);
};
